// Backend lifecycle controls for the selected profile.
// Mirrors the desktop's BackendStatusCard visually: status dot + label +
// start/stop/restart buttons. Live state comes from WS backend.stateChanged.
import { useState } from "react";
import type { BackendSupervisorState } from "@shared/api/schemas/backend";
import { ApiError, httpClient } from "../../client/http-client";
import { PlayIcon, RefreshIcon, StopIcon } from "../../components/Icons";

interface BackendStatusCardProps {
	profileId: string;
	state: BackendSupervisorState | null;
}

type UiStatus = "idle" | "starting" | "running" | "stopping" | "error";

function resolveStatus(state: BackendSupervisorState | null): UiStatus {
	if (!state) return "idle";
	if (state.status === "disabled") return "idle";
	return state.status;
}

function labelFor(status: UiStatus): string {
	switch (status) {
		case "idle":
			return "Not running";
		case "starting":
			return "Starting...";
		case "running":
			return "Running";
		case "stopping":
			return "Stopping...";
		case "error":
			return "Error";
		default:
			return status;
	}
}

function dotClassFor(status: UiStatus): string {
	switch (status) {
		case "running":
			return "tg-status-dot-success";
		case "starting":
		case "stopping":
			return "tg-status-dot-warning";
		case "error":
			return "tg-status-dot-error";
		default:
			return "tg-status-dot-muted";
	}
}

export const BackendStatusCard = ({
	profileId,
	state,
}: BackendStatusCardProps) => {
	const [busy, setBusy] = useState<"start" | "stop" | "restart" | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);

	const status = resolveStatus(state);
	const canStart = status === "idle" || status === "error";
	const canStop = status === "running" || status === "starting";
	const canRestart = status === "running" || status === "error";

	const run = async (
		action: "start" | "stop" | "restart",
		fn: () => Promise<unknown>,
	) => {
		setBusy(action);
		setActionError(null);
		try {
			await fn();
		} catch (err) {
			setActionError(err instanceof ApiError ? err.message : String(err));
		} finally {
			setBusy(null);
		}
	};

	return (
		<section className="tg-surface rounded-2xl p-4">
			<header className="flex items-center justify-between gap-3">
				<div className="flex items-center gap-3">
					<span
						className={`inline-block h-2.5 w-2.5 rounded-full ${dotClassFor(status)}`}
						aria-hidden="true"
					/>
					<div>
						<h3 className="tg-text-primary text-[0.95rem] font-semibold leading-5">
							Backend runtime
						</h3>
						<p className="tg-text-muted text-[0.82rem] leading-5">
							{labelFor(status)}
							{state?.pid !== null && state?.pid !== undefined
								? ` · PID ${state.pid}`
								: ""}
							{state?.backendUrl ? ` · ${state.backendUrl}` : ""}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={() =>
							void run("start", () => httpClient.backend.start(profileId))
						}
						disabled={!canStart || busy !== null}
						className="tg-btn-secondary tg-text-secondary inline-flex h-9 items-center gap-2 rounded-xl px-3 text-[0.85rem] font-semibold disabled:cursor-not-allowed disabled:opacity-55"
					>
						<PlayIcon className="h-4 w-4" />
						<span>{busy === "start" ? "Starting..." : "Start"}</span>
					</button>
					<button
						type="button"
						onClick={() =>
							void run("stop", () => httpClient.backend.stop(profileId))
						}
						disabled={!canStop || busy !== null}
						className="tg-btn-secondary tg-text-secondary inline-flex h-9 items-center gap-2 rounded-xl px-3 text-[0.85rem] font-semibold disabled:cursor-not-allowed disabled:opacity-55"
					>
						<StopIcon className="h-4 w-4" />
						<span>{busy === "stop" ? "Stopping..." : "Stop"}</span>
					</button>
					<button
						type="button"
						onClick={() =>
							void run("restart", () => httpClient.backend.restart(profileId))
						}
						disabled={!canRestart || busy !== null}
						className="tg-btn-secondary tg-text-secondary inline-flex h-9 items-center gap-2 rounded-xl px-3 text-[0.85rem] font-semibold disabled:cursor-not-allowed disabled:opacity-55"
					>
						<RefreshIcon className="h-4 w-4" />
						<span>{busy === "restart" ? "Restarting..." : "Restart"}</span>
					</button>
				</div>
			</header>

			{state?.message ? (
				<div className="tg-text-muted mt-3 text-[0.82rem] leading-5">
					{state.message}
				</div>
			) : null}
			{actionError ? (
				<div
					className="tg-alert-error mt-3 rounded-lg px-3 py-2 text-[0.85rem]"
					role="alert"
				>
					{actionError}
				</div>
			) : null}
		</section>
	);
};
